<template>
  <div class="responsive-data-table">
    <!-- Vista de tabla para desktop -->
    <div class="desktop-table">
      <v-data-table
        v-bind="$attrs"
        :headers="headers"
        :items="items"
        :loading="loading"
      >
        <!-- Pasar todos los slots al componente hijo -->
        <template v-for="(_, slot) in $slots" v-slot:[slot]="scope" :key="slot">
          <slot :name="slot" v-bind="scope || {}" />
        </template>
      </v-data-table>
    </div>

    <!-- Vista de cards para móvil -->
    <div class="mobile-cards">
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
        class="ma-4"
      ></v-progress-circular>
      
      <div v-else-if="!items || items.length === 0" class="text-center py-8">
        <v-icon icon="mdi-alert" color="warning" size="large"></v-icon>
        <p class="text-subtitle-1 mt-2">No hay datos disponibles</p>
      </div>
      
      <div v-else class="mobile-cards-container">
        <v-card
          v-for="(item, itemIndex) in items"
          :key="`item-${itemIndex}-${item.id || itemIndex}`"
          class="mobile-card mb-3"
          elevation="2"
          :ripple="false"
        >
          <v-card-text class="pb-2">
            <template v-for="(header, headerIndex) in mobileHeaders" :key="`row-${itemIndex}-${headerIndex}`">
              <div class="mobile-card-row" v-if="header && header.key && header.title">
                <div class="mobile-card-label">
                  {{ header.title }}:
                </div>
                <div class="mobile-card-value">
                  <!-- Usar slot personalizado si existe -->
                  <slot
                    v-if="$slots[`item.${header.key}`]"
                    :name="`item.${header.key}`"
                    :item="item"
                    :value="getNestedValue(item, header.key)"
                  />
                  <!-- Valor por defecto -->
                  <span v-else>{{ getNestedValue(item, header.key) }}</span>
                </div>
              </div>
            </template>
          </v-card-text>
          
          <!-- Acciones si existen -->
          <v-card-actions v-if="hasActions" class="pt-0">
            <v-spacer></v-spacer>
            <slot name="item.actions" :item="item" />
          </v-card-actions>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResponsiveDataTable',
  props: {
    headers: {
      type: Array,
      required: true,
      default: () => []
    },
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    mobileHeaders() {
      if (!this.headers || !Array.isArray(this.headers)) {
        return []
      }
      // Filtrar headers que no sean de acciones para mostrar en cards
      return this.headers.filter(header => 
        header && 
        header.key && 
        header.title && 
        header.key !== 'actions'
      )
    },
    hasActions() {
      if (!this.headers || !Array.isArray(this.headers)) {
        return false
      }
      return this.headers.some(header => header && header.key === 'actions')
    }
  },
  methods: {
    getNestedValue(obj, path) {
      if (!obj || !path) return 'N/A'
      
      // Si el path contiene puntos, navegar por el objeto anidado
      if (path.includes('.')) {
        return path.split('.').reduce((current, key) => {
          return current && current[key] !== undefined ? current[key] : 'N/A'
        }, obj)
      }
      
      // Acceso directo a la propiedad
      return obj[path] !== undefined ? obj[path] : 'N/A'
    }
  }
}
</script>

<style scoped>
.responsive-data-table {
  width: 100%;
}

.desktop-table {
  display: block;
}

.mobile-cards {
  display: none;
}

.mobile-cards-container {
  max-width: 100%;
}

.mobile-card {
  border-radius: 12px;
  transition: all 0.2s ease;
}

.mobile-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.mobile-card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
}

.mobile-card-row:last-child {
  border-bottom: none;
}

.mobile-card-label {
  font-weight: 600;
  color: #666;
  font-size: 0.85rem;
  flex: 0 0 40%;
  min-width: 120px;
}

.mobile-card-value {
  flex: 1;
  text-align: right;
  font-size: 0.9rem;
}

/* Responsive breakpoint */
@media (max-width: 768px) {
  .desktop-table {
    display: none;
  }
  
  .mobile-cards {
    display: block;
  }
}

/* Ajustes para pantallas muy pequeñas */
@media (max-width: 480px) {
  .mobile-card-row {
    flex-direction: column;
    align-items: flex-start;
    padding: 8px 0;
  }
  
  .mobile-card-label {
    flex: none;
    margin-bottom: 4px;
    font-size: 0.8rem;
  }
  
  .mobile-card-value {
    text-align: left;
    width: 100%;
    font-size: 0.85rem;
  }
}

/* Estilos para chips y elementos especiales en cards */
.mobile-card-value :deep(.v-chip) {
  font-size: 0.75rem;
  height: 24px;
}

.mobile-card-value :deep(.v-btn) {
  font-size: 0.8rem;
  min-width: auto;
  padding: 0 8px;
}
</style> 